"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { applyFollowChange, getFollowList, type FollowListKind, type FollowListPage } from "./api";

/**
 * One of a profile's two lists, a page at a time. Keyed by the viewer too: `follow_state` is
 * read for whoever is signed in, so a sign-in (or out) reads it again. A follow from a card
 * updates that card in place — its state and its follower count — never a re-read.
 */
export function useFollowList(username, kind, enabled) {
  const $ = _c(20);
  const viewer = useAuthStore(_temp);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== kind || $[1] !== username || $[2] !== viewer) {
    t0 = ["follow-list", username, kind, viewer];
    $[0] = kind;
    $[1] = username;
    $[2] = viewer;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0 as const;
  let t1;
  if ($[4] !== kind || $[5] !== username) {
    t1 = t2 => {
      const {
        pageParam
      } = t2;
      return getFollowList(username, kind, pageParam);
    };
    $[4] = kind;
    $[5] = username;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== enabled || $[8] !== queryKey || $[9] !== t1) {
    t2 = {
      queryKey,
      queryFn: t1,
      initialPageParam: 1,
      getNextPageParam: _temp2,
      enabled,
      staleTime: 60000
    };
    $[7] = enabled;
    $[8] = queryKey;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const query = useInfiniteQuery(t2);
  let t3;
  if ($[11] !== query.data?.pages) {
    const seen = new Set();
    t3 = (query.data?.pages ?? []).flatMap(_temp3).filter(p_0 => seen.has(p_0.id) ? false : (seen.add(p_0.id), true));
    $[11] = query.data?.pages;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const people = t3;
  const total = query.data?.pages[0]?.page.total ?? null;
  let t4;
  if ($[13] !== queryClient) {
    t4 = (username_0, next) => {
      queryClient.setQueriesData({
        queryKey: ["follow-list"]
      }, data => data && {
        ...data,
        pages: data.pages.map(p_1 => ({
          ...p_1,
          artists: p_1.artists.map(a => a.username === username_0 ? applyFollowChange(a, next) : a)
        }))
      });
    };
    $[13] = queryClient;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const onFollowChange = t4;
  let t5;
  if ($[15] !== onFollowChange || $[16] !== people || $[17] !== query || $[18] !== total) {
    t5 = {
      query,
      people,
      total,
      onFollowChange
    };
    $[15] = onFollowChange;
    $[16] = people;
    $[17] = query;
    $[18] = total;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function _temp3(p) {
  return p.artists;
}
function _temp2(last) {
  return last.page.current_page < last.page.last_page ? last.page.current_page + 1 : undefined;
}
function _temp(s) {
  return s.user?.id ?? null;
}